import { PropType, defineComponent } from "vue";
import jquery from "jquery";

import { ITypeaheadOptions } from "../../vue-interfaces/i-typeahead";
import { ITaskByUserResponse } from "../../../services/task.service";
import { debounce } from "../../../common/vue-helpers/debounce";

import ScrollPagination from "../ScrollPagination/ScrollPagination.vue";
import ProductPill from "../ProductPill/ProductPill.vue";

export default defineComponent({
    components: {
        ScrollPagination,
        ProductPill
    },

    props: {
        disabled: { type: Boolean, default: false },
        name: { type: String, default: "" },
        selectedItems: { type: Array as PropType<ITypeaheadOptions[]>, default: [] },
        onFetch: { type: Function },
        fetchUpdatedCount: { type: Number, default: 0 },
        placeholder: { type: String, default: null },
        isCalendar: { type: Boolean, default: false },
        isMap: { type: Boolean, default: false },
        isUnassigned: { type: Boolean, default: false },
        classes: { type: String, default: null },
        customMessage: { type: String, default: null },
        tags: { default: [] }
    },

    emits: ["selectedItemsUpdated"],

    data() {
        return {
            filteredTasks: { type: Array as PropType<ITaskByUserResponse[]> },
            fetchedItems: [] as ITypeaheadOptions[],
            cacheFetchedItems: [] as ITypeaheadOptions[],
            displayItems: [] as ITypeaheadOptions[],
            suggestedItems: [] as ITypeaheadOptions[],
            selectedOptions: [] as any,
            open: false as boolean,
            message: "" as string,
            search: "" as string,
            calendarSearch: "" as string,
            searchTermDebounce: null,
            characterLimit: 0 as number,
            userOs: "",
            pageNumber: 1
        };
    },

    async mounted() {
        this.userOs = window.sessionStorage.getItem("userOs");
        // Prevents the drop down from closing on inside click
        // But allow the close button to bleed the original handler.
        jquery(this.$refs.dropdownElement).on("click.bs.dropdown", (e: any) => {
            if (e.target.nodeName != "BUTTON") {
                e.stopPropagation();
            }
        });

        jquery(this.$refs.dropdown).on("show.bs.dropdown", (e) => {
            this.onDropdownOpen();
            this.open = true;
        });

        jquery(this.$refs.dropdown).on("hide.bs.dropdown", (e) => {
            this.open = false;
        });
        if (!this.isUnassigned) {
            await this.onCredentialsFetch();
        }
        this.updateMessage();
        this.searchTermDebounce = debounce(this.onCredentialsFetch, 500);
    },

    watch: {
        selectedItems(newValue: any) {
            if (!newValue.length) {
                this.selectedOptions = [];
            } else {
                this.selectedOptions = newValue;
            }
            this.selectedListUpdated();
        },

        fetchUpdatedCount() {
            // Added this parameter to catch the changes in dropdown options
            if (this.fetchUpdatedCount > 0) {
                this.selectedOptions = [];
                this.onCredentialsFetch();
                this.updateMessage();
            }
        },

        calendarSearch(newValue, oldValue) {
            if (newValue !== oldValue) {
                (jquery(this.$refs.dropdown).find('[data-bs-toggle="dropdown"]') as any).dropdown("show");
                this.search = newValue;
                this.searchTermDebounce();
            }
        }
    },

    computed: {
        getIsSearching() {
            return this.search;
        },

        getInputPlaceholder() {
            if (this.placeholder) {
                return this.placeholder;
            } else {
                return `Search for ${this.name}`;
            }
        },

        uniqueFetchedItems() {
            const seen = new Set();
            return this.fetchedItems.filter((item) => {
                if (seen.has(item.value)) {
                    return false;
                }
                seen.add(item.value);
                return true;
            });
        },
        uniqueSuggestedItems() {
            const seen = new Set();
            return this.suggestedItems.filter((item) => {
                if (seen.has(item.value)) {
                    return false;
                }
                seen.add(item.value);
                return true;
            });
        },
        getSelectedEmployees() {
            return this.selectedItems.slice(0, 3).map((emp: any) => {
                return {
                    ...emp,
                    name: this.isMap
                        ? emp.name.split(" ")[0].slice(0, 5).concat("...")
                        : emp.name.split(",")[0].slice(0, 5).concat("...")
                };
            });
        }
    },

    methods: {
        toggleSelection() {
            this.selectedOptions = [];
            this.$emit("selectedItemsUpdated", this.selectedOptions);
            this.selectedListUpdated();
        },

        async onDropdownOpen() {
            // this.calendarSearch = "";
            this.search = "";
            this.displayItems = this.selectedItems.filter(() => true); // Copies array.
            this.suggestedItems = this.fetchedItems.filter(
                (x) => this.displayItems.find((y) => y.value == x.value && y.name == x.name) == null
            );
            this.onCredentialsFetch();
        },

        async onCredentialsFetch(pageToLoad?: number) {
            if (this.characterLimit > 0 && this.search.length < this.characterLimit) {
                this.fetchedItems = [];
                this.suggestedItems = [];
            } else {
                if (this.onFetch && typeof this.onFetch === "function") {
                    if (typeof pageToLoad == "number") {
                        this.cacheFetchedItems = await this.onFetch(this.search, pageToLoad);
                    } else {
                        this.cacheFetchedItems = await this.onFetch(this.search);
                    }
                    this.fetchedItems = this.cacheFetchedItems;
                    this.suggestedItems = this.cacheFetchedItems?.filter(
                        (x: any) => this.displayItems.find((y) => y.value == x.value && y.name == x.name) == null
                    );
                }
            }
        },

        updateSelectedItems() {
            this.$emit("selectedItemsUpdated", this.selectedOptions);
            this.calendarSearch = "";
        },

        selectedListUpdated() {
            // update displayItems and suggestedItems list if user unselect an item.
            if (this.displayItems.length > this.selectedItems.length) {
                let diffItems = this.displayItems.filter(
                    (x) => this.selectedItems.find((y) => y.value == x.value && y.name == x.name) == null
                );
                this.displayItems = this.displayItems.filter(
                    (x) => diffItems.find((y) => y.value == x.value && y.name == x.name) == null
                );
                this.suggestedItems = this.cacheFetchedItems;
                for (let item of diffItems) {
                    this.suggestedItems.filter((suggestedItems) => suggestedItems.name === item.name);
                }
            } else {
                let newItems = this.selectedItems.filter(
                    (x) => this.displayItems.find((y) => y.value == x.value && y.name == x.name) == null
                );
                for (let item of newItems) {
                    this.displayItems.push(item);
                }
            }
            this.suggestedItems = this.suggestedItems.filter(
                (x) => this.displayItems.find((y) => y.value == x.value && y.name == x.name) == null
            );
            this.updateMessage();
        },

        updateMessage() {
            if ((!this.selectedItems || this.selectedItems.length == 0) && this.customMessage?.length > 0) {
                this.message = this.customMessage;
                return;
            }
            if (!this.selectedItems || this.selectedItems.length == 0) {
                this.message = "All Selected";
            }
            if (this.selectedItems.length == 1) {
                this.message = this.selectedItems[0].name;
            }
            if (this.selectedItems.length > 1) {
                this.message = `${this.selectedItems.length} Selected`;
            }
        },

        pageNumberUpdated(pageToLoad: number) {
            if (!pageToLoad) {
                return;
            }
            if (pageToLoad != this.pageNumber) {
                this.pageNumber = pageToLoad;
            } else {
                return;
            }
            this.onCredentialsFetch(pageToLoad);
        },

        removeUser(e: any, item: any) {
            e.stopPropagation();
            this.selectedOptions = this.selectedOptions.filter((option: any) => option.value !== item.value);
            this.$emit("selectedItemsUpdated", this.selectedOptions);
        }
    }
});
